import { gql, useQuery } from '@apollo/client';
import { Box, type SxProps, type Theme } from '@mui/material';
import { useEffect, useState } from 'react';
import type {
  ComponentDefinition,
  ComponentEditProps,
  ComponentViewProps,
  UseDataType,
} from '@/admins/SmartReports/components/Components/types';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';

export const COMPONENT_ID = 'hero_image_background';
const CDN_URL = 'https://app.cdn.welligence.com/misc/hero';
const DEFAULT_HERO_IMAGE = 'default.png';

// we need the ID for Apollo to cache properly
const QUERY_ASSET = gql`
  query assetV2($input: EntityIdentifierInput!) {
    assetV2(input: $input) {
      id
      country {
        name
        isoCode
      }
    }
  }
`;

const QUERY_COUNTRY = gql`
  query country($input: EntityIdentifierInput!) {
    country(input: $input) {
      id
      name
      isoCode
    }
  }
`;

const styles = {
  root: {
    position: 'relative',
    backgroundColor: (theme: Theme) => theme.palette.baseColors.deepSeaNavy.c100,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    padding: '1rem',
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

export const getHeroImageUrl = (countryIsoCode: string | undefined) => {
  return countryIsoCode ? `${CDN_URL}/${countryIsoCode}.JPG` : `${CDN_URL}/${DEFAULT_HERO_IMAGE}`;
};

const useData = ({ portfolio, persistedData, onComponentLoad }: UseDataType<string>) => {
  const [dataFetched, setDataFetched] = useState(persistedData);

  const assetId = portfolio?.assets?.[0];
  const countryId = portfolio?.countries?.[0];

  const {
    data: assetData,
    loading: assetLoading,
    error: assetError,
  } = useQuery(QUERY_ASSET, {
    variables: { input: { id: assetId } },
    // skip if there's no assetId or if the data is already loaded
    skip: !assetId || !!dataFetched,
  });

  const {
    data: countryData,
    loading: countryLoading,
    error: countryError,
  } = useQuery(QUERY_COUNTRY, {
    variables: { input: { id: countryId } },
    // skip if there's no countryId or if the data is already loaded
    skip: !countryId || !!dataFetched,
  });

  useEffect(() => {
    if (dataFetched) return;

    // This countryIsoCode may be null, but then we'll just use the default image
    const countryIsoCode = assetData?.assetV2?.country?.isoCode || countryData?.country?.isoCode;
    setDataFetched(countryIsoCode);
    if (onComponentLoad) {
      onComponentLoad({
        componentId: COMPONENT_ID,
        // Save isoCode instead of full URL to make the component more resilient to CDN URL changes
        data: countryIsoCode,
      });
    }
  }, [dataFetched, assetData, countryData, onComponentLoad]);

  return {
    data: dataFetched,
    loading: assetLoading || countryLoading,
    error: assetError || countryError,
  };
};

const HeroImageBackgroundView = ({ data, sx, children }: ComponentViewProps<string>) => {
  const url = getHeroImageUrl(data);
  const combinedSx = {
    ...styles.root,
    ...sx,
    backgroundImage: `url(${url})`,
  } as SxProps;

  return <Box sx={combinedSx}>{children}</Box>;
};

const HeroImageBackground = ({
  portfolio,
  persistedData,
  onComponentLoad,
  sx,
  children,
}: ComponentEditProps<string>) => {
  const { data, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  if (loading) {
    return <Box sx={styles.root}>Loading...</Box>;
  }

  if (error) {
    return <Box sx={styles.root}>Error: {error.message}</Box>;
  }

  return (
    <HeroImageBackgroundView sx={sx} data={data || ''}>
      {children}
    </HeroImageBackgroundView>
  );
};

export const HeroImageBackgroundComponent: ComponentDefinition<string> = {
  id: COMPONENT_ID,
  name: 'Hero Image Background',
  type: SmartReportComponentTypesEnum.Image,
  reportTypes: Object.values(SmartReportTypeEnum),
  Thumbnail: HeroImageBackground,
  Component: HeroImageBackground,
  ComponentView: HeroImageBackgroundView,
};
